import { computed, defineComponent, h, ref, watch } from 'vue';
import { d, t } from '@/ui/i18n/i18n';
import { box, column, indexView, level, loader, tag, textColumn } from '@/ui/components';
import { bulkDelete, remove } from '@/services/api/resources/issue/v2';
import { list as getIssueList } from '@/services/api/resources/todo';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { emptyList } from '@/services/api/response/types';
import { IssuePriority, TodoIssueRecord } from '@/entities/issue';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import * as qs from '@/ui/queryState';
import { bulkDeleteButton, Button } from '@/ui/components/Button';
import { filterListToOptions, sequence } from '@/ui/helpers/uiHelper';
import { issuePriority, issueStatusToText } from '@/ui/dictionary';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import {
  assigneeFilter,
  issueOverdueAllFilter,
  issueStatusFilter,
  lastUpdatedFilter,
  searchFilter,
  entityItemFilter,
  todoPriorityFilter,
} from '@/ui/components/filters/filters-horizontal';
import { IssueStatus, Status, TodoList } from '@/entities/todo';
import { SelectOptions, SortDirection } from '@/ui/types';
import { pagination } from '@/ui/components/Pagination';
import { useIssueCheck } from '@/ui/pages/issue/useIssueCheck';
import IssueEmpty from './IssueEmpty.vue';
import { filterFormLoader } from '@/ui/components/Loader';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { Tooltip } from '@/ui/components/Tooltip';
import { pageTitleTooltiped } from '@/ui/components/PageTitle';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { issueStatusOptions, issueStatusOptionsOverdue } from '@/ui/components/filters/options';
import { SortingIcon } from '@/ui/components/icon/SortingIcon';
import { issueIndexExport } from '@/ui/pages/issue/IssueIndexExport';
import { SupportedExportFormatEnum } from '@/services/api/resources/issue/exports';
import { useIssue } from '@/ui/concerns/issue/useIssue';

export const IssueIndexPage = defineComponent({
  name: 'IssueIndexPage',
  setup() {
    if (!hasFeature('issues')) {
      return renderResourceTeaserPage(t('common.issues'));
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { checkResult, performCheck } = useIssueCheck();

    const { sort, dir, onSortChange } = useSorting('createdAt', SortDirection.Descending);
    const search = qs.string('q', '');
    const assignee = qs.numbers('assignee', []);
    const entityItem = qs.numbers('entityItem', []);
    const status = qs.numericEnums('status', Object.values(Status) as Status[], []);
    const highPriority = qs.boolean('high', false);
    const onlyOverdue = qs.nullableBoolean('overdue', null);
    const issueStatus = qs.numericEnums('issueStatus', Object.values(IssueStatus) as IssueStatus[], []);
    const start = qs.nullableDate('start', null);
    const end = qs.nullableDate('end', null);

    const overdueSelected = computed(() => onlyOverdue.value !== null);
    const filteredIssueStatusOptions = ref<SelectOptions<number>>(issueStatusOptions());

    const calculateIssueStatusesBasedOnOverdue = (overdueSelectedVal: boolean) => {
      const issueStatusNotSelected = issueStatus.value.length === 0;
      //when overdue, show only pending and started status
      const allowedOverdueStatuses = [IssueStatus.Pending, IssueStatus.InProgress];
      //pass only allowed to query or selected
      //and filter selected to contain only allowed
      const selectedIssueStatuses = overdueSelectedVal && issueStatusNotSelected
        ? allowedOverdueStatuses
        : issueStatus.value.filter(selectedIssueStatus => allowedOverdueStatuses.includes(selectedIssueStatus));

      filteredIssueStatusOptions.value = overdueSelectedVal ? issueStatusOptionsOverdue() : issueStatusOptions();
      issueStatus.value = selectedIssueStatuses;
    };

    calculateIssueStatusesBasedOnOverdue(overdueSelected.value);
    watch(overdueSelected, calculateIssueStatusesBasedOnOverdue);

    // get filters task
    const users = ref<SelectOptions>([]);
    const entityItems = ref<SelectOptions>([]);
    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllEntityItems()),
      ([userList, entityItemList]) => {
        users.value = filterListToOptions(userList);
        entityItems.value = filterListToOptions(entityItemList);
      },
    );

    const query = () => ({
      page: page.value,
      perPage: perPage.value,
      sort: sort.value,
      dir: dir.value,
      search: search.value,
      assignee: assignee.value,
      entityItem: entityItem.value,
      status: status.value,
      issueStatus: issueStatus.value,
      priority: highPriority.value ? [IssuePriority.High] : [],
      updatedAtStart: start.value,
      updatedAtEnd: end.value,
      overdue: onlyOverdue.value,
    });
    // get list task
    const list = ref<TodoList>(emptyList());
    const getList = useTask(
      () => getIssueList(query()),
      listResponse => {
        list.value = listResponse;
      },
    );
    watch([
      page,
      perPage,
      search,
      assignee,
      entityItem,
      start,
      end,
      issueStatus,
      onlyOverdue,
      highPriority,
      sort,
      dir,
    ], getList.run);

    watch(() => getList.state.value.pending, newVal => {
      initialLoading.value = newVal;
    });

    const { issueStatusToTagType, priorityToTagType, hasAssignees, joinAssignees } = useIssue();

    const issueStatusTag = (s: IssueStatus) => tag(
      issueStatusToTagType[s],
      issueStatusToText(s),
    );

    const todoPriorityTag = (s: IssuePriority) => tag(
      priorityToTagType[s],
      issuePriority(s),
    );

    const todoDueTag = (issue: TodoIssueRecord) => {
      if (!issue.data.dueAt || issue.data.issue.status === IssueStatus.Done) {
        return '';
      }

      const deadlineInThePast = issue.data.dueAt.getTime() < (new Date).getTime();

      return deadlineInThePast ? tag('error', t('common.overdue')) : '';
    };

    const {
      selectedResources,
      deleteBulkTask,
      onCheckedRowsUpdate,
      isBulkDeletionDisabled,
    } = useBulkDeletable<TodoIssueRecord>({
      getList,
      bulkDeleteFn: bulkDelete,
      mapIds: record => record.data.issue.id,
    });

    return function render() {
      const issueAssigneesToTags = (issue: TodoIssueRecord) => {
        return hasAssignees(issue)
          ? joinAssignees(issue)
          : [tag('purple', t('common.no-assignee'))];
      };

      return loader('div', performCheck, [
        level([
          pageTitleTooltiped(t('common.issues'), t('feature-explanation.issues')),
        ], [
          hasPermission(Permissions.CREATE_ISSUE) ? h(Button, {
            attrs: {
              tag: 'router-link',
              to: { name: 'issueCreate' },
              iconLeft: 'plus',
            },
          }, t('issues.create')) : null,
        ]),
        checkResult.value
          ? indexView([
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search, resetPagination),
              lastUpdatedFilter(start, end, resetPagination),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
              assigneeFilter(assignee, users.value, resetPagination),
              issueStatusFilter(issueStatus, resetPagination, filteredIssueStatusOptions.value),
              issueOverdueAllFilter(onlyOverdue, resetPagination),
              todoPriorityFilter(highPriority, resetPagination),
              issueIndexExport({
                props: {
                  query: query(),
                  formats: [SupportedExportFormatEnum.CSV, SupportedExportFormatEnum.XLSX],
                },
              }),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    editRoute: 'issueEdit',
                    entityIdPath: ['data', 'issue', 'id'],
                    list: list.value.data,
                    deleteFn: remove,
                    sort: sort.value,
                    dir: dir.value,
                    sortableTitle: true,
                    paginationLinks: list.value.meta,
                    loading: deleteBulkTask.state.value.pending,
                    selectedRecords: selectedResources.value,
                    recordName: {
                      plural: t('common.issues'),
                      singular: t('common.issue'),
                    },
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                    sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                      onSortChange(field, direction);
                      resetPagination();
                    }),
                  },
                  scopedSlots: {
                    title: ({ row: issue }: { row: TodoIssueRecord }) => [
                      issue.data.title,
                    ],
                    default: () => [
                      column({
                        field: 'issueStatus',
                        label: t('common.status'),
                        sortable: true,
                      }, {
                        default: ({ row: issue }: { row: TodoIssueRecord }) => issueStatusTag(issue.data.issue.status),
                      }),
                      column(
                        {
                          field: 'assignees',
                          label: t('common.assignees'),
                        }, {
                          default: ({ row: issue }: { row: TodoIssueRecord }) => issueAssigneesToTags(issue),
                        },
                      ),
                      column({
                        field: 'entityItem',
                        label: t('common.entity-item'),
                        sortable: true,
                      }, {
                        default: ({ row: issue }: { row: TodoIssueRecord }) =>
                          issue.data.entityItem ? issue.data.entityItem.data.title : null,
                      }),
                      column({
                        field: 'dueAt',
                        label: t('common.deadline'),
                        sortable: true,
                      }, {
                        default: ({ row: issue }: { row: TodoIssueRecord }) => issue.data.dueAt
                          ? [d(issue.data.dueAt), ' ', todoDueTag(issue)]
                          : [tag('purple', t('common.no-deadline'))],
                      }),
                      textColumn({
                        field: 'lastUpdateAt',
                        label: t('common.last-updated'),
                        sortable: true,
                      },
                      {
                        default: ({ row: issue }: { row: TodoIssueRecord }) =>
                          d(issue.data.issue.updatedAt, 'shortDateTime'),
                        header: () => h('div', { class: 'is-relative' }, [
                          h('span', t('common.last-updated')),
                          h(Tooltip, {
                            props: {
                              label: t('feature-explanation.last-updated'),
                              multilined: true,
                            },
                          }, [
                            h('div', [
                              featureExplanationIcon({ classes: 'ml-1' }),
                            ]),
                          ]),
                          h(SortingIcon, {
                            props: {
                              field: 'lastUpdateAt',
                              sort: sort.value,
                              direction: dir.value,
                            },
                          }),
                        ]),
                      }),
                      column({
                        field: 'issuePriority',
                        label: t('common.priority'),
                        sortable: true,
                      }, {
                        default: ({ row: issue }: { row: TodoIssueRecord }) =>
                          todoPriorityTag(issue.data.issue.priority),
                      }),
                    ],
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ])
          : box([h(IssueEmpty)]),
      ], false);
    };
  },
});
